/** Appointment changes are authored with the standard date fields in the Visit's timezone. */
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ActivityEntityName, MetadataEntityFieldType, SectionLayout, SurfaceType, VISIT_PAST_RETENTION_DAYS, visitAppointmentPassed, VisitBrowseRow } from '@jms/model';
import { VisitDateOverride, visitRescheduleAllowed, visitRescheduleFloor } from '@jms/model/entities/planned.work/visit/visit.dates';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { defineSurface, defineSurfaceSchema, WbSurfaceComponent } from '@workbuddy/ui/v2/surface.engine';
import { DateTime } from 'luxon';

/** Shown wherever a past date can be picked, so the server's refusal is never a surprise. */
export function visitRescheduleRuleText(now: Date, timezone?: string): string {

  const cutoff = DateTime.fromJSDate(now, { zone: timezone }).startOf('day').minus({ days: VISIT_PAST_RETENTION_DAYS }).toFormat('d LLL yyyy');
  return `A past date must be on or after the visit's original date, and no earlier than ${cutoff}.`;
}

interface VisitAppointmentModel {
  date: Date;
  startTime: Date;
  endTime: Date;
  allDay: boolean;
}

@Component({
  selector: 'wb-visit-dates-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WbSurfaceComponent],
  template: '<wb-surface [definition]="$definition()" />',
})
export class VisitDatesDialogComponent {
  private readonly dialog = injectDialogRef<VisitDateOverride>();
  public readonly $row = input.required<VisitBrowseRow>({ alias: 'row' });
  /** Dispatch of a passed appointment: the person confirms or moves the date before a Job is built. */
  public readonly $decision = input(false, { alias: 'decision' });
  /** Approve and move: the same picker, submitted as one approval of the moved appointment. */
  public readonly $approve = input(false, { alias: 'approve' });

  protected readonly $definition = computed(() => {
    const row = this.$row();
    const decision = this.$decision();
    const approve = this.$approve();
    const now = new Date();
    const floor = visitRescheduleFloor(row, now);
    const pastDateRules = visitRescheduleRuleText(now, row.timezone);
    // Only a passed appointment can land on a past date, so only then do the past-date rules apply.
    const description = decision
      ? `This visit's appointment has passed. Keep the date to confirm it happened then, or pick a new one. ${pastDateRules}`
      : visitAppointmentPassed(row, now) ? pastDateRules : null;
    const startDate = new Date(row.startDate);
    const endDate = new Date(row.endDate);
    const initialModel: VisitAppointmentModel = {
      date: DateTime.fromJSDate(startDate, { zone: row.timezone }).startOf('day').toJSDate(),
      startTime: startDate,
      endTime: endDate > startDate ? endDate : DateTime.fromJSDate(startDate, { zone: row.timezone }).plus({ days: 1 }).toJSDate(),
      allDay: row.allDay === true,
    };
    const schema = defineSurfaceSchema(ActivityEntityName.Visit, SurfaceType.Form)
      .model<VisitAppointmentModel>()
      .submitLabel(decision ? 'Confirm date' : approve ? 'Approve and move' : 'Reschedule this visit')
      .body(body => body.section('dates', section => {
        section
          .title(`${decision ? 'Confirm appointment' : approve ? 'Approve and move visit' : 'Reschedule visit'} · ${row.timezone}`)
          .layout(SectionLayout.PropertyList)
          .columns(1)
          .field({ bind: 'date', required: true, autofocus: true, metadata: { type: MetadataEntityFieldType.Date, displayName: 'Date' }, editorInputs: { timezone: row.timezone, min: floor } })
          .field({ bind: 'startTime', required: true, metadata: { type: MetadataEntityFieldType.Time, displayName: 'Start time' }, editorInputs: { timezone: row.timezone } })
          .field({ bind: 'endTime', required: true, metadata: { type: MetadataEntityFieldType.Time, displayName: 'End time' }, editorInputs: { timezone: row.timezone } })
          .field({ bind: 'allDay', metadata: { type: MetadataEntityFieldType.Boolean, displayName: 'All day', readonly: false } });
        if (description) section.description(description);
      }))
      .rules(rules => rules.rule('all-day-times', rule => rule.when(condition => condition.eq('allDay', true))
        .then(effect => effect.hide({ type: 'field', id: 'startTime' }), effect => effect.hide({ type: 'field', id: 'endTime' }))))
      .build();

    return defineSurface<VisitAppointmentModel>({
      schema,
      initialModel,
      mode: 'create',
      onSubmit: event => {
        const date = DateTime.fromJSDate(new Date(event.model.date), { zone: row.timezone }).startOf('day');
        if (!date.isValid || !visitRescheduleAllowed(row, date.toISODate()!, now)) throw new Error(pastDateRules);
        let start = date;
        let end = date.plus({ days: 1 });
        if (!event.model.allDay) {
          const startTime = DateTime.fromJSDate(new Date(event.model.startTime), { zone: row.timezone });
          const endTime = DateTime.fromJSDate(new Date(event.model.endTime), { zone: row.timezone });
          if (!startTime.isValid || !endTime.isValid) throw new Error('Choose valid start and end times');
          start = date.set({ hour: startTime.hour, minute: startTime.minute });
          end = date.set({ hour: endTime.hour, minute: endTime.minute });
          // An overnight attendance finishes the following day; the user still picks only one date.
          if (end < start) end = end.plus({ days: 1 });
        }
        if (!start.isValid || !end.isValid || end <= start) throw new Error('End time must be after the start time');
        const dates: VisitDateOverride = { startDate: start.toJSDate(), endDate: end.toJSDate(), allDay: event.model.allDay };
        this.dialog.close(dates);
        return event.model;
      },
      onCancel: () => this.dialog.dismiss(),
    });
  });
}
